import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

import PrivateRoute from "./components/PrivateRoute";
import PlatformAdminRoute from "./components/PlatformAdminRoute";
import UserManagementRoute from "./components/UserManagementRoute";
import DashboardRouter from "./components/Dashboard/DashboardRouter";
import SessionTimeoutManager from "./components/auth/SessionTimeoutManager";
import useCurrentUserAccess from "./hooks/useCurrentUserAccess";
import API_BASE_URL from "./config";

import Home from "./pages/Home";
import Login from "./pages/Login";
import VerifyLogin from "./pages/VerifyLogin";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import MicrosoftCallback from "./pages/MicrosoftCallback";
import ActivateAccount from "./pages/ActivateAccount";

import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/AdminDashboard";

import SalesLeads from "./pages/SalesLeads";
import NewSalesLead from "./pages/NewSalesLead";
import SalesLeadProfile from "./pages/SalesLeadProfile";

import Subscribers from "./pages/Subscribers";
import NewSubscriber from "./pages/NewSubscriber";
import SubscriberProfile from "./pages/SubscriberProfile";

import DemoAccounts from "./pages/DemoAccounts";
import NewDemoAccount from "./pages/NewDemoAccount";
import DemoAccountProfile from "./pages/DemoAccountProfile";

import Leads from "./pages/Leads";
import NewLead from "./pages/NewLead";
import LeadProfile from "./pages/LeadProfileFinal";

import Clients from "./pages/Clients";
import NewClient from "./pages/NewClient";
import ClientProfile from "./pages/ClientProfile";

import Users from "./pages/Users";
import NewUser from "./pages/NewUser";
import UserProfile from "./pages/UserProfile";

import Calendar from "./pages/Calendar";
import Email from "./pages/Email";
import Tasks from "./pages/Tasks";
import Policies from "./pages/Policies";
import Opportunities from "./pages/Opportunities";
import ReportsPage from "./pages/ReportsPage";
import ReportBuilderPage from "./pages/ReportBuilderPage";
import Support from "./pages/Support";
import SupportDashboard from "./pages/support-admin/SupportDashboard";
import SupportTickets from "./pages/support-admin/SupportTickets";
import SupportTicketDetail from "./pages/support-admin/SupportTicketDetail";
import SupportAdminRoute from "./components/SupportAdminRoute";


import Settings from "./pages/Settings";
import CarrierList from "./pages/settings/CarrierList";
import Carriers from "./pages/settings/Carriers";
import CoverageTypes from "./pages/settings/CoverageTypes";
import PolicyStatus from "./pages/settings/PolicyStatus";
import CustomFields from "./pages/settings/CustomFields";
import SystemEmails from "./pages/settings/SystemEmails";
import SystemEmailEditor from "./pages/settings/SystemEmailEditor";
import SubscriptionPlans from "./pages/settings/SubscriptionPlans";
import SubscriptionPlanEditor from "./pages/settings/SubscriptionPlanEditor";
import Products from "./pages/Products";
import ImportContacts from "./pages/settings/ImportContacts";

import Templates from "./pages/Templates";
import TemplateEditor from "./pages/TemplateEditor";
import SendTestEmail from "./pages/SendTestEmail";
import WorkflowsPage from "./pages/Automations/WorkflowsPage";
import WorkflowEditor from "./pages/Automations/WorkflowEditor";
import WorkflowActionBuilder from "./pages/Automations/WorkflowActionBuilder";

import SOAVerifyPage from "./components/SOA/SOAVerifyPage";
import SOACompletePage from "./components/SOA/SOACompletePage";
import ACAConsentPage from "./components/ACAConsent/ACAConsentPage";
import BookMePublicPage from "./pages/BookMePublicPage";

import "./components/profile-components/Navbar.css";
import "./pages/Settings.css";
import "./pages/LeadProfile.css";
import "./styles/Responsive.css";
import "./App.css";

const OutlookRedirect = () => {
  window.location.href = "https://agent-book-crm-backend.onrender.com/api/connect/outlook";
  return <p style={{ padding: "2rem", textAlign: "center" }}>Redirecting to Microsoft login...</p>;
};

const AppHostRedirect = () => {
  useEffect(() => {
    const hostname = window.location.hostname.toLowerCase();
    const pathname = window.location.pathname;
    const search = window.location.search || "";

    const isRootOrWww =
      hostname === "agentbookcrm.com" ||
      hostname === "www.agentbookcrm.com";

    const appOnlyPaths = [
      "/login",
      "/verify-login",
      "/forgot-password",
      "/reset-password",
      "/activate-account",
      "/dashboard",
      "/dashboard-home",
      "/admin-dashboard",
      "/sales-leads",
      "/new-sales-lead",
      "/sales-lead-profile",
      "/subscribers",
      "/new-subscriber",
      "/subscriber-profile",
      "/demo-accounts",
      "/leads",
      "/new-lead",
      "/lead-profile",
      "/clients",
      "/new-client",
      "/client-profile",
      "/users",
      "/calendar",
      "/email",
      "/tasks",
      "/policies",
      "/opportunities",
      "/reports",
      "/support",
      "/support-admin",
      "/support-admin/tickets",
      "/settings",
      "/settings/carriers",
      "/settings/coverage-types",
      "/settings/policy-statuses",
      "/settings/custom-fields",
      "/settings/system-emails",
      "/settings/subscription-plans",
      "/settings/imports",
      "/carriers",
      "/products",
      "/templates",
      "/send-test-email",
      "/workflows",
      "/automations",
      "/outlook/connect",
      "/auth/microsoft/callback"
    ];

    const shouldRedirect =
      isRootOrWww &&
      appOnlyPaths.some((path) => pathname === path || pathname.startsWith(`${path}/`));

    if (shouldRedirect) {
      window.location.replace(`https://app.agentbookcrm.com${pathname}${search}`);
    }
  }, []);

  return null;
};

function FeatureAccessDenied({ featureKey }) {
  const featureLabels = {
    bookme: "BookMe",
    team_users: "Team Users",
    agency_dashboard: "Agency Dashboard",
    reporting: "Reports",
    commission_module: "Commission Module",
    automated_workflows: "Workflows"
  };

  return (
    <div className="main-content">
      <div style={{ maxWidth: "720px", margin: "4rem auto", padding: "2rem", background: "#ffffff", borderRadius: "12px", boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)", textAlign: "center" }}>
        <h1 style={{ marginBottom: "0.75rem" }}>Feature Not Included</h1>
        <p style={{ color: "#475569", lineHeight: 1.6 }}>
          {featureLabels[featureKey] || "This feature"} is not included in your current subscription plan.
          Please contact your account administrator or Agent Book CRM support if you believe this is incorrect.
        </p>
      </div>
    </div>
  );
}

function FeatureRoute({ featureKey, children }) {
  const { loading, platformRole } = useCurrentUserAccess();

  const [featureKeys, setFeatureKeys] = useState([]);
  const [featuresLoaded, setFeaturesLoaded] = useState(false);

  const isPlatformUser =
    platformRole?.key_name === "platform_admin" ||
    platformRole?.key_name === "platform_employee";

  useEffect(() => {
    let isMounted = true;

    async function loadFeatureAccess() {
      try {
        setFeaturesLoaded(false);

        if (isPlatformUser) {
          if (isMounted) {
            setFeatureKeys([]);
            setFeaturesLoaded(true);
          }
          return;
        }

        const token = localStorage.getItem("token");

        if (!token) {
          if (isMounted) {
            setFeatureKeys([]);
            setFeaturesLoaded(true);
          }
          return;
        }

        const res = await fetch(`${API_BASE_URL}/api/subscription-plans/my-features`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Failed to load subscription features");
        }

        if (isMounted) {
          setFeatureKeys(Array.isArray(data.feature_keys) ? data.feature_keys : []);
          setFeaturesLoaded(true);
        }
      } catch (err) {
        console.error("Feature route access check failed:", err.message);

        if (isMounted) {
          setFeatureKeys([]);
          setFeaturesLoaded(true);
        }
      }
    }

    if (!loading) {
      loadFeatureAccess();
    }

    return () => {
      isMounted = false;
    };
  }, [loading, isPlatformUser]);

  if (loading || !featuresLoaded) {
    return (
      <div className="main-content">
        <div style={{ padding: "2rem" }}>Loading feature access...</div>
      </div>
    );
  }

  if (isPlatformUser) {
    return children;
  }

  if (!featureKeys.includes(featureKey)) {
    return <FeatureAccessDenied featureKey={featureKey} />;
  }

  return children;
}

function SettingsCarriersRoute() {
  const { loading, platformRole } = useCurrentUserAccess();

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ padding: "2rem" }}>Loading carrier settings...</div>
      </div>
    );
  }

  if (platformRole?.key_name === "platform_admin") {
    return <CarrierList />;
  }

  return <Carriers />;
}

function App() {
  return (
    <Router>
      <AppHostRedirect />
      <SessionTimeoutManager />

      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/verify-login" element={<VerifyLogin />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/activate-account" element={<ActivateAccount />} />
        <Route path="/auth/microsoft/callback" element={<MicrosoftCallback />} />
        <Route path="/outlook/connect" element={<OutlookRedirect />} />

        <Route path="/soa/verify/:token" element={<SOAVerifyPage />} />
        <Route path="/aca-consent/:token" element={<ACAConsentPage />} />
        <Route path="/bookme/:profileSlug" element={<BookMePublicPage />} />
        <Route path="/soa/complete/:token" element={<PrivateRoute><SOACompletePage /></PrivateRoute>} />

        <Route path="/sales-leads" element={<PrivateRoute><SalesLeads /></PrivateRoute>} />
        <Route path="/new-sales-lead" element={<PrivateRoute><NewSalesLead /></PrivateRoute>} />
        <Route path="/sales-lead-profile/:id" element={<PrivateRoute><SalesLeadProfile /></PrivateRoute>} />

        <Route path="/subscribers" element={<PrivateRoute><Subscribers /></PrivateRoute>} />
        <Route path="/new-subscriber" element={<PrivateRoute><NewSubscriber /></PrivateRoute>} />
        <Route path="/subscriber-profile/:id" element={<PrivateRoute><SubscriberProfile /></PrivateRoute>} />

        <Route path="/demo-accounts" element={<PrivateRoute><PlatformAdminRoute><DemoAccounts /></PlatformAdminRoute></PrivateRoute>} />
        <Route path="/demo-accounts/new" element={<PrivateRoute><PlatformAdminRoute><NewDemoAccount /></PlatformAdminRoute></PrivateRoute>} />
        <Route path="/demo-accounts/:id" element={<PrivateRoute><PlatformAdminRoute><DemoAccountProfile /></PlatformAdminRoute></PrivateRoute>} />

        <Route path="/dashboard" element={<PrivateRoute><DashboardRouter /></PrivateRoute>} />
        <Route path="/dashboard-home" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/admin-dashboard" element={<PrivateRoute><PlatformAdminRoute><AdminDashboard /></PlatformAdminRoute></PrivateRoute>} />

        <Route path="/support-admin" element={<PrivateRoute><SupportAdminRoute><SupportDashboard /></SupportAdminRoute></PrivateRoute>} />
        <Route path="/support-admin/tickets" element={<PrivateRoute><SupportAdminRoute><SupportTickets /></SupportAdminRoute></PrivateRoute>} />
        <Route path="/support-admin/tickets/:ticketId" element={<PrivateRoute><SupportAdminRoute><SupportTicketDetail /></SupportAdminRoute></PrivateRoute>} />

        <Route path="/leads" element={<PrivateRoute><Leads /></PrivateRoute>} />
        <Route path="/new-lead" element={<PrivateRoute><NewLead /></PrivateRoute>} />
        <Route path="/lead-profile/:id" element={<PrivateRoute><LeadProfile /></PrivateRoute>} />

        <Route path="/clients" element={<PrivateRoute><Clients /></PrivateRoute>} />
        <Route path="/new-client" element={<PrivateRoute><NewClient /></PrivateRoute>} />
        <Route path="/client-profile/:id" element={<PrivateRoute><ClientProfile /></PrivateRoute>} />

        <Route path="/calendar" element={<PrivateRoute><Calendar /></PrivateRoute>} />
        <Route path="/email" element={<PrivateRoute><Email /></PrivateRoute>} />
        <Route path="/tasks" element={<PrivateRoute><Tasks /></PrivateRoute>} />
        <Route path="/policies" element={<PrivateRoute><Policies /></PrivateRoute>} />
        <Route path="/opportunities" element={<PrivateRoute><Opportunities /></PrivateRoute>} />
        <Route path="/reports" element={<PrivateRoute><FeatureRoute featureKey="reporting"><ReportsPage /></FeatureRoute></PrivateRoute>} />
        <Route path="/reports/:templateKey" element={<PrivateRoute><FeatureRoute featureKey="reporting"><ReportBuilderPage /></FeatureRoute></PrivateRoute>} />
        <Route path="/support" element={<PrivateRoute><Support /></PrivateRoute>} />

        <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
        <Route path="/settings/carriers" element={<PrivateRoute><SettingsCarriersRoute /></PrivateRoute>} />
        <Route path="/settings/coverage-types" element={<PrivateRoute><CoverageTypes /></PrivateRoute>} />
        <Route path="/settings/policy-statuses" element={<PrivateRoute><PolicyStatus /></PrivateRoute>} />
        <Route path="/settings/custom-fields" element={<PrivateRoute><CustomFields /></PrivateRoute>} />
        <Route path="/settings/system-emails" element={<PrivateRoute><PlatformAdminRoute><SystemEmails /></PlatformAdminRoute></PrivateRoute>} />
        <Route path="/settings/system-emails/new" element={<PrivateRoute><PlatformAdminRoute><SystemEmailEditor /></PlatformAdminRoute></PrivateRoute>} />
        <Route path="/settings/system-emails/:id" element={<PrivateRoute><PlatformAdminRoute><SystemEmailEditor /></PlatformAdminRoute></PrivateRoute>} />
        <Route path="/settings/subscription-plans" element={<PrivateRoute><PlatformAdminRoute><SubscriptionPlans /></PlatformAdminRoute></PrivateRoute>} />
        <Route path="/settings/subscription-plans/new" element={<PrivateRoute><PlatformAdminRoute><SubscriptionPlanEditor /></PlatformAdminRoute></PrivateRoute>} />
        <Route path="/settings/subscription-plans/:id" element={<PrivateRoute><PlatformAdminRoute><SubscriptionPlanEditor /></PlatformAdminRoute></PrivateRoute>} />
        <Route path="/settings/imports" element={<PrivateRoute><ImportContacts /></PrivateRoute>} />

        <Route path="/users" element={<PrivateRoute><UserManagementRoute><Users /></UserManagementRoute></PrivateRoute>} />
        <Route path="/users/:id" element={<PrivateRoute><UserManagementRoute><UserProfile /></UserManagementRoute></PrivateRoute>} />
        <Route path="/new-user" element={<PrivateRoute><UserManagementRoute><NewUser /></UserManagementRoute></PrivateRoute>} />
        <Route path="/user-profile/:id" element={<PrivateRoute><UserManagementRoute><UserProfile /></UserManagementRoute></PrivateRoute>} />

        <Route path="/carriers" element={<PrivateRoute><PlatformAdminRoute><CarrierList /></PlatformAdminRoute></PrivateRoute>} />
        <Route path="/products" element={<PrivateRoute><Products /></PrivateRoute>} />

        <Route path="/templates" element={<PrivateRoute><Templates /></PrivateRoute>} />
        <Route path="/templates/new" element={<PrivateRoute><TemplateEditor /></PrivateRoute>} />
        <Route path="/templates/:id" element={<PrivateRoute><TemplateEditor /></PrivateRoute>} />
        <Route path="/send-test-email" element={<PrivateRoute><SendTestEmail /></PrivateRoute>} />

        <Route path="/automations" element={<PrivateRoute><FeatureRoute featureKey="automated_workflows"><WorkflowsPage /></FeatureRoute></PrivateRoute>} />
        <Route path="/workflows" element={<PrivateRoute><FeatureRoute featureKey="automated_workflows"><WorkflowsPage /></FeatureRoute></PrivateRoute>} />
        <Route path="/automations/workflows/new" element={<PrivateRoute><FeatureRoute featureKey="automated_workflows"><WorkflowEditor /></FeatureRoute></PrivateRoute>} />
        <Route path="/automations/workflows/:id" element={<PrivateRoute><FeatureRoute featureKey="automated_workflows"><WorkflowEditor /></FeatureRoute></PrivateRoute>} />
        <Route path="/automations/workflows/:id/actions/new" element={<PrivateRoute><FeatureRoute featureKey="automated_workflows"><WorkflowActionBuilder /></FeatureRoute></PrivateRoute>} />
        <Route path="/automations/workflows/:id/actions/:actionId" element={<PrivateRoute><FeatureRoute featureKey="automated_workflows"><WorkflowActionBuilder /></FeatureRoute></PrivateRoute>} />
      </Routes>
    </Router>
  );
}

export default App;